import { createContext, use, useEffect, useMemo, useState, type ReactNode } from 'react'

/** How the current site wants people sent to B2C. */
export interface SiteSignInOptions {
  /** The domain hint B2C gets, in place of the one taken from the typed address. */
  readonly domainHint: string | null
  /** The B2C policy to sign in through, used only when the site has no domain hint. */
  readonly customB2CPolicy: string | null
  /** A one-time sign-in link's hint, B2C's `id_token_hint`, sent with its own policy alone (SM-39). */
  readonly idTokenHint?: string | null
}

interface SiteSignIn {
  readonly options: SiteSignInOptions | null
  /** The options once the site's answer is in, loaded or failed; until then a sign-in could miss them. */
  readonly whenResolved: () => Promise<SiteSignInOptions | null>
}

// With no provider there is no site to wait for.
const SiteSignInOptionsContext = createContext<SiteSignIn>({
  options: null,
  whenResolved: () => Promise.resolve(null),
})

/** Gives sign-in the current site's options. Site Management provides them once the site has loaded. */
export function SiteSignInOptionsProvider(props: {
  readonly options: SiteSignInOptions | null
  readonly resolved: boolean
  readonly children: ReactNode
}) {
  const { options, resolved } = props
  // One answer for every sign-in started before it came.
  const [answer] = useState(() => {
    let settle: (value: SiteSignInOptions | null) => void = () => undefined
    const promise = new Promise<SiteSignInOptions | null>((resolve) => {
      settle = resolve
    })
    return { promise, settle }
  })

  useEffect(() => {
    if (resolved) {
      answer.settle(options)
    }
  }, [answer, resolved, options])

  const value = useMemo<SiteSignIn>(
    () => ({ options, whenResolved: () => (resolved ? Promise.resolve(options) : answer.promise) }),
    [answer, options, resolved],
  )
  return <SiteSignInOptionsContext value={value}>{props.children}</SiteSignInOptionsContext>
}

/** The current site's sign-in options, or `null` until the site has loaded or when it could not. */
export function useSiteSignInOptions(): SiteSignInOptions | null {
  return use(SiteSignInOptionsContext).options
}

/** Waits for the current site's answer, so a sign-in started before it still takes its options. */
export function useSiteSignInOptionsWhenResolved(): () => Promise<SiteSignInOptions | null> {
  return use(SiteSignInOptionsContext).whenResolved
}
